import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bold, 
  Italic, 
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  RotateCcw,
  AlignJustify
} from 'lucide-react';
import { TypographyDropdown } from './TypographyDropdown';

interface TextToolbarProps {
  isVisible: boolean;
  position: { top: number; left: number };
  textStyles: any;
  onStyleChange: (key: string, value: any) => void;
  isGlobalDarkMode: boolean;
  elementType: string;
  onElementTypeChange: (type: string) => void;
  textElementWidth?: number;
  textElementHeight?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  getDefaultStyles?: (elementType: string) => any;
  headingTypes?: { value: string; label: string; size: number }[];
  paragraphTypes?: { value: string; label: string; size: number }[];
}

export const TextToolbar: React.FC<TextToolbarProps> = ({
  isVisible,
  position,
  textStyles,
  onStyleChange,
  isGlobalDarkMode,
  elementType,
  onElementTypeChange,
  textElementWidth = 0,
  textElementHeight = 0,
  onMouseEnter,
  onMouseLeave,
  getDefaultStyles,
  headingTypes: propsHeadingTypes,
  paragraphTypes: propsParagraphTypes
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showElementDropdown, setShowElementDropdown] = useState(false);
  const [showTextCaseDropdown, setShowTextCaseDropdown] = useState(false);
  const [showLineHeightDropdown, setShowLineHeightDropdown] = useState(false);
  const [showKerningDropdown, setShowKerningDropdown] = useState(false);
  const [showFontSizeInput, setShowFontSizeInput] = useState(false);
  const [showAlignmentOptions, setShowAlignmentOptions] = useState(false);
  const [customFontSize, setCustomFontSize] = useState(textStyles.fontSize || 18);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Element type options based on whether it's a heading or paragraph
  const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(elementType);
  
  const headingTypes = propsHeadingTypes || [
    { value: 'h1', label: 'H1', size: 128 },
    { value: 'h2', label: 'H2', size: 96 },
    { value: 'h3', label: 'H3', size: 72 },
    { value: 'h4', label: 'H4', size: 56 },
    { value: 'h5', label: 'H5', size: 48 },
    { value: 'h6', label: 'H6', size: 36 }
  ];

  const paragraphTypes = propsParagraphTypes || [
    { value: 'p-large', label: 'XL', size: 32 },
    { value: 'p-medium', label: 'L', size: 24 },
    { value: 'p', label: 'M', size: 18 },
    { value: 'p-small', label: 'S', size: 14 }
  ];

  const elementTypes = isHeading ? headingTypes : paragraphTypes;

  // Text case options with icons
  const textCaseOptions = [
    { value: 'none', label: 'Aa' },
    { value: 'uppercase', label: 'AA' },
    { value: 'lowercase', label: 'aa' },
    { value: 'capitalize', label: 'Ab' }
  ];

  // Line height options with values in labels
  const lineHeightOptions = [
    { value: 1.0, label: 'Tight 1.0' },
    { value: 1.1, label: 'Snug 1.1' },
    { value: 1.2, label: 'Normal 1.2' },
    { value: 1.3, label: 'Relaxed 1.3' },
    { value: 1.4, label: 'Loose 1.4' },
    { value: 1.5, label: 'Extra 1.5' }
  ];

  // Kerning options (letter-spacing)
  const kerningOptions = [
    { value: '-0.05em', label: 'Tight -0.05em' },
    { value: '-0.025em', label: 'Snug -0.025em' },
    { value: '0em', label: 'Normal 0em' },
    { value: '0.025em', label: 'Relaxed 0.025em' },
    { value: '0.05em', label: 'Loose 0.05em' },
    { value: '0.1em', label: 'Extra 0.1em' }
  ];

  // Calculate dynamic toolbar position to ensure it stays on screen
  useEffect(() => {
    if (isVisible && toolbarRef.current) {
      const toolbarRect = toolbarRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const textVerticalPosition = position.top / viewportHeight;
      
      let newTop = position.top - 60; // Default: above the text
      let newLeft = position.left;
      
      // Determine dropdown direction based on text position
      // If text is in bottom 50% of screen, dropdowns should open upward
      const shouldOpenUpward = textVerticalPosition > 0.5;
      setDropdownDirection(shouldOpenUpward ? 'up' : 'down');
      
      // Adjust horizontal position if toolbar would go off-screen
      if (newLeft + toolbarRect.width > viewportWidth - 20) {
        newLeft = viewportWidth - toolbarRect.width - 20;
      }
      if (newLeft < 20) {
        newLeft = 20;
      }
      
      // Adjust vertical position if toolbar would go off-screen
      if (newTop < 20) {
        newTop = position.top + textElementHeight + 10; // Below the text
      }
      if (newTop + toolbarRect.height > viewportHeight - 20) {
        newTop = viewportHeight - toolbarRect.height - 20;
      }
      
      setToolbarPosition({ top: newTop, left: newLeft });
    }
  }, [isVisible, position, textElementWidth, textElementHeight]);

  // Update local state when textStyles change
  useEffect(() => {
    setCustomFontSize(parseInt(textStyles.fontSize) || 18);
  }, [textStyles.fontSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
        setShowElementDropdown(false);
        setShowTextCaseDropdown(false);
        setShowLineHeightDropdown(false);
        setShowKerningDropdown(false);
        setShowFontSizeInput(false);
        setShowAlignmentOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle element type change
  const handleElementTypeChange = (type: string) => {
    if (type === 'custom') {
      setShowFontSizeInput(!showFontSizeInput);
      setShowElementDropdown(false);
    } else {
      const elementData = elementTypes.find(el => el.value === type);
      if (elementData) {
        onElementTypeChange(type);
        onStyleChange('fontSize', elementData.size);
        setCustomFontSize(elementData.size);
      }
      setShowElementDropdown(false);
    }
  };

  // Handle font size change with input
  const handleFontSizeInputChange = (value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 300) {
      setCustomFontSize(numValue);
      onStyleChange('fontSize', numValue);
    }
  };

  // Get current element type label
  const getCurrentElementLabel = () => {
    // Check if current fontSize matches any element type
    const currentSize = parseInt(textStyles.fontSize);
    const matchingType = elementTypes.find(type => type.size === currentSize);
    
    if (matchingType) {
      return matchingType.label;
    }
    
    // Fallback to element type or default
    const current = elementTypes.find(type => type.value === elementType);
    return current ? current.label : (isHeading ? 'H1' : 'M');
  };

  // Get current text case icon
  const getCurrentTextCaseIcon = () => {
    const current = textCaseOptions.find(option => option.value === textStyles.textTransform);
    return current ? current.label : 'Aa';
  };

  // Get current line height label
  const getCurrentLineHeightLabel = () => {
    return textStyles.lineHeight || 1.2;
  };

  // Get current kerning label
  const getCurrentKerningLabel = () => {
    return textStyles.letterSpacing || '0em';
  };


  // Reset styles to original values
  const handleResetStyles = () => {
    // Get default styles using the function passed from EditableText
    const defaultStyles = getDefaultStyles ? getDefaultStyles(elementType) : {
      fontFamily: 'inherit',
      fontSize: isHeading ? (elementType === 'h1' ? 128 : elementType === 'h2' ? 96 : elementType === 'h3' ? 72 : elementType === 'h4' ? 56 : elementType === 'h5' ? 48 : 36) : 18,
      textAlign: 'left',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textTransform: 'none',
      lineHeight: 1.2,
      letterSpacing: '0em',
      color: '#000000'
    };
    
    // Reset all styles to defaults
    Object.keys(defaultStyles).forEach(key => {
      onStyleChange(key, defaultStyles[key as keyof typeof defaultStyles]);
    });
    setCustomFontSize(parseInt(defaultStyles.fontSize.toString()) || 18);
    
    // Close all dropdowns
    setShowColorPicker(false);
    setShowElementDropdown(false);
    setShowTextCaseDropdown(false);
    setShowLineHeightDropdown(false);
    setShowKerningDropdown(false);
    setShowFontSizeInput(false);
    setShowAlignmentOptions(false);
  };


  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={toolbarRef}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="fixed z-50 rounded-lg shadow-lg border border-border backdrop-blur-lg bg-card/95"
        style={{
          top: toolbarPosition.top,
          left: toolbarPosition.left
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="flex items-center px-2 py-1 space-x-1">
          
          {/* Element Type Dropdown - Show actual value */}
          <div className="relative">
            <button
              onClick={() => setShowElementDropdown(!showElementDropdown)}
              className="flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors text-foreground hover:bg-muted"
            >
              <span>{getCurrentElementLabel()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showElementDropdown && (
              <div className={`absolute ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              } left-0 rounded-md border border-border shadow-lg min-w-[80px] z-60 bg-card`}>
                {elementTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleElementTypeChange(type.value)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                      parseInt(textStyles.fontSize) === type.size
                        ? 'bg-workspace-accent/20 text-workspace-accent'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
                <button
                  onClick={() => handleElementTypeChange('custom')}
                  className="w-full px-3 py-2 text-left text-sm transition-colors text-foreground hover:bg-accent"
                >
                  Custom
                </button>
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Font Family Dropdown - Compact */}
          <div className="min-w-[100px]">
            <TypographyDropdown
              value={textStyles.fontFamily}
              onChange={(font) => onStyleChange('fontFamily', font)}
              isGlobalDarkMode={isGlobalDarkMode}
              placeholder="Font"
              className="text-xs"
              dropdownDirection={dropdownDirection}
            />
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Font Size Input - Only show when custom is selected */}
          {showFontSizeInput && (
            <>
              <div className="flex items-center space-x-1">
                <input
                  type="number"
                  value={customFontSize}
                  onChange={(e) => handleFontSizeInputChange(e.target.value)}
                  className="w-12 px-1 py-1 text-xs rounded border border-border text-center bg-background text-foreground"
                  min="8"
                  max="300"
                />
                <span className={`text-xs ${
                  'text-muted-foreground'
                }`}>px</span>
              </div>
              {/* Separator */}
              <div className="w-px h-4 bg-border" />
            </>
          )}

          {/* Line Height Dropdown - Show value in label */}
          <div className="relative">
            <button
              onClick={() => setShowLineHeightDropdown(!showLineHeightDropdown)}
              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                'text-foreground hover:bg-muted'
              }`}
            >
              <AlignJustify className="w-3 h-3" />
              <span>{getCurrentLineHeightLabel()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showLineHeightDropdown && (
              <div className={`absolute ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              } left-0 rounded-md border border-border shadow-lg min-w-[120px] z-60 bg-card`}>
                {lineHeightOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onStyleChange('lineHeight', option.value);
                      setShowLineHeightDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                      textStyles.lineHeight === option.value
                        ? 'bg-workspace-accent/20 text-workspace-accent'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Kerning Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowKerningDropdown(!showKerningDropdown)}
              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                'text-foreground hover:bg-muted'
              }`}
            >
              <span className="text-xs font-mono">Aa</span>
              <span>{getCurrentKerningLabel()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showKerningDropdown && (
              <div className={`absolute ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              } left-0 rounded-md border border-border shadow-lg min-w-[140px] z-60 bg-card`}>
                {kerningOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onStyleChange('letterSpacing', option.value);
                      setShowKerningDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                      textStyles.letterSpacing === option.value
                        ? 'bg-workspace-accent/20 text-workspace-accent'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Style Controls - Compact */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onStyleChange('fontWeight', textStyles.fontWeight === 'bold' ? 'normal' : 'bold')}
              className={`p-1 rounded transition-colors ${
                textStyles.fontWeight === 'bold'
                  ? 'bg-workspace-accent text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Bold className="w-3 h-3" />
            </button>
            <button
              onClick={() => onStyleChange('fontStyle', textStyles.fontStyle === 'italic' ? 'normal' : 'italic')}
              className={`p-1 rounded transition-colors ${
                textStyles.fontStyle === 'italic'
                  ? 'bg-workspace-accent text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Italic className="w-3 h-3" />
            </button>
            <button
              onClick={() => onStyleChange('textDecoration', textStyles.textDecoration === 'underline' ? 'none' : 'underline')}
              className={`p-1 rounded transition-colors ${
                textStyles.textDecoration === 'underline'
                  ? 'bg-workspace-accent text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Underline className="w-3 h-3" />
            </button>
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Alignment Controls - Show current, expand on hover */}
          <div 
            className="relative flex items-center"
            onMouseEnter={() => setShowAlignmentOptions(true)}
            onMouseLeave={() => setShowAlignmentOptions(false)}
          >
            {/* Container that adjusts width based on hover state */}
            <div className={`flex items-center relative overflow-hidden transition-all duration-200 ${
              showAlignmentOptions ? 'space-x-1' : 'space-x-0'
            }`}>
              {/* Left Align */}
              <button
                onClick={() => onStyleChange('textAlign', 'left')}
                className={`p-1 rounded transition-all duration-200 ${
                  textStyles.textAlign === 'left'
                    ? 'bg-blue-500 text-white'
                    : showAlignmentOptions
                      ? isGlobalDarkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      : 'opacity-0 pointer-events-none w-0 p-0 overflow-hidden'
                }`}
              >
                <AlignLeft className="w-3 h-3" />
              </button>
              
              {/* Center Align */}
              <button
                onClick={() => onStyleChange('textAlign', 'center')}
                className={`p-1 rounded transition-all duration-200 ${
                  textStyles.textAlign === 'center'
                    ? 'bg-blue-500 text-white'
                    : showAlignmentOptions
                      ? isGlobalDarkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      : 'opacity-0 pointer-events-none w-0 p-0 overflow-hidden'
                }`}
              >
                <AlignCenter className="w-3 h-3" />
              </button>
              
              {/* Right Align */}
              <button
                onClick={() => onStyleChange('textAlign', 'right')}
                className={`p-1 rounded transition-all duration-200 ${
                  textStyles.textAlign === 'right'
                    ? 'bg-blue-500 text-white'
                    : showAlignmentOptions
                      ? isGlobalDarkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      : 'opacity-0 pointer-events-none w-0 p-0 overflow-hidden'
                }`}
              >
                <AlignRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Text Case Dropdown with Icons */}
          <div className="relative">
            <button
              onClick={() => setShowTextCaseDropdown(!showTextCaseDropdown)}
              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                'text-foreground hover:bg-muted'
              }`}
            >
              <span className="font-medium">{getCurrentTextCaseIcon()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showTextCaseDropdown && (
              <div className={`absolute ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              } left-0 rounded-md border border-border shadow-lg min-w-[80px] z-60 bg-card`}>
                {textCaseOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onStyleChange('textTransform', option.value);
                      setShowTextCaseDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-center ${
                      textStyles.textTransform === option.value
                        ? 'bg-workspace-accent/20 text-workspace-accent'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Color Picker - Compact */}
          <div className="relative">
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={`p-1 rounded transition-colors ${
                isGlobalDarkMode 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <div 
                className="w-4 h-4 rounded border-0"
                style={{ backgroundColor: textStyles.color }}
              />
            </button>

            {showColorPicker && (
              <div className={`absolute ${
                dropdownDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'
              } right-0 p-2 rounded-md border border-border shadow-lg z-60 bg-card`}>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={textStyles.color}
                    onChange={(e) => onStyleChange('color', e.target.value)}
                    className="w-6 h-6 rounded border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={textStyles.color}
                    onChange={(e) => onStyleChange('color', e.target.value)}
                    className={`w-16 px-1 py-1 text-xs rounded border-0 ${
                      isGlobalDarkMode
                        ? 'bg-gray-700 text-white'
                        : 'bg-background text-foreground'
                    }`}
                    placeholder="#000000"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-border" />

          {/* Reset Button */}
          <button
            onClick={handleResetStyles}
            className={`p-1 rounded transition-colors ${
              isGlobalDarkMode 
                ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="Reset to default styles"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};